import type { DeviceCatalogSnapshot } from "@eweadn-qiankun/shared-types";
import { deviceMatchesActiveBrand } from "../main/brandDeviceFilter";
import { getSupportedDevicesSync } from "../main/supportedDevicesStore";
import {
  buildDeviceCatalogSnapshot,
  readDeviceCatalogSnapshotFromPayload,
  resolveCatalogRowFromSnapshot,
} from "./deviceCatalogSnapshot";
import { formatDeviceDisplayLabel } from "../main/formatDeviceDisplayLabel";
import {
  normalizeEquipmentName,
  resolveKeyboardCatalogRowForEquipment,
  resolveMouseCatalogRowForEquipment,
} from "../musicRhythm/supportedKeyboardMatch";

export interface CommunityDeviceCatalogOption {
  /** 配置广场型号键（与连接页 `buildDeviceCatalogKey` 一致，按配置表行唯一定位） */
  key: string;
  /** 下拉展示文案 */
  label: string;
  displayname: string;
  snapshot: DeviceCatalogSnapshot;
}

const CATALOG_KEY_PREFIX = "catalog:";
const MODEL_KEY_PREFIX = `${CATALOG_KEY_PREFIX}model:`;

function resolveRowDisplayname(row: Record<string, unknown>): string {
  return String(row.displayname ?? row.productName ?? "").trim();
}

/** 同 displayname 多行时优先有线主型号，忽略 2.4G / 8K 接收器行 */
function isWirelessOrDongleRow(row: Record<string, unknown>): boolean {
  const workMode = String(row.workMode ?? "").trim();
  if (workMode === "2") return true;
  if (row.is4G === true) return true;

  const productName = String(row.productName ?? "").trim();
  if (/8K/i.test(productName)) return true;
  if (Number(row.type) === 5) return true;

  return false;
}

function pickCanonicalCatalogRow(
  rows: Record<string, unknown>[]
): Record<string, unknown> | null {
  if (!rows.length) return null;
  const wired = rows.filter((row) => !isWirelessOrDongleRow(row));
  return wired[0] ?? rows[0]!;
}

/**
 * 配置表「同一型号、不同连接方式」分组键：与各方案商在
 * `keyboardCatalogRowMatchesEquipment` / `mouseCatalogRowMatchesEquipment` 中的消歧字段对齐，
 * 不用 displayname 单独区分（如 X98 1K / X98 8K / 华丰达 X98）。
 */
function catalogProductLineIdentity(
  row: Record<string, unknown>,
  category: "keyboard" | "mouse"
): string {
  const solution = String(row.solutionRovider ?? "").trim();
  const parts: string[] = [
    resolveRowDisplayname(row),
    String(row.vendorId ?? ""),
    solution,
  ];

  if (category === "keyboard") {
    parts.push(String(row.keyboardType ?? ""));
    if (
      solution === "xingshan" ||
      solution === "freq" ||
      solution === "freqC"
    ) {
      parts.push(normalizeEquipmentName(row.productName));
    }
    if (solution === "huafenda") {
      parts.push(normalizeEquipmentName(row.productName));
      parts.push(normalizeEquipmentName(row.displayname));
    }
    return parts.join("\0");
  }

  parts.push(String(row.deviceId ?? ""));
  if (solution === "jiangmeng") {
    parts.push(
      String(row.customerId ?? ""),
      String(row.moudleId ?? ""),
      String(row.type ?? "")
    );
  }
  if (solution === "freq" || solution === "freqC") {
    parts.push(normalizeEquipmentName(row.productName));
  }
  return parts.join("\0");
}

/** @deprecated 仅无 `_deviceCatalog` 的旧配置兜底 */
export function buildCommunityModelCatalogKey(
  category: "keyboard" | "mouse",
  displayname: string
): string {
  const name = displayname.trim();
  if (!name) return "";
  return `${MODEL_KEY_PREFIX}${category}:${name}`;
}

/** 实机快照级唯一键（分享 payload / 社区型号筛选） */
export function buildDeviceCatalogKey(snapshot: DeviceCatalogSnapshot): string {
  const identifier = String(snapshot.identifier ?? "").trim();
  if (identifier) {
    return `${CATALOG_KEY_PREFIX}id:${identifier}`;
  }

  const parts = [
    snapshot.vendorId,
    snapshot.productId,
    String(snapshot.solutionRovider ?? "").trim(),
    String(snapshot.keyboardType ?? snapshot.deviceId ?? "").trim(),
    String(snapshot.productName ?? "").trim(),
  ];
  return `${CATALOG_KEY_PREFIX}eq:${parts.join(":")}`;
}

export function isDeviceCatalogKey(value: string): boolean {
  return value.startsWith(CATALOG_KEY_PREFIX);
}

export function deviceCatalogSnapshotsMatch(
  a: DeviceCatalogSnapshot | null | undefined,
  b: DeviceCatalogSnapshot | null | undefined
): boolean {
  if (!a || !b) return false;
  return buildDeviceCatalogKey(a) === buildDeviceCatalogKey(b);
}

function listCommunityCatalogRows(
  category: "keyboard" | "mouse" | "earphone"
): Record<string, unknown>[] {
  const rows = getSupportedDevicesSync()[category];
  if (!Array.isArray(rows)) return [];
  return (rows as Record<string, unknown>[]).filter(
    isCommunitySupportedCatalogRow
  );
}

/** 配置表已支持设备（不要求 communitySupported，供关联应用等场景解析型号键） */
function listSupportedDeviceCatalogRows(
  category: "keyboard" | "mouse" | "earphone"
): Record<string, unknown>[] {
  return (
    getSupportedDevicesSync()[category] as Record<string, unknown>[]
  ).filter(
    (row) =>
      row.isSupported !== false &&
      deviceMatchesActiveBrand(row as Record<string, unknown>)
  );
}

/** 配置社区可用：已适配、未关闭 communitySupported，且属于当前打包品牌 */
export function isCommunitySupportedCatalogRow(
  row: Record<string, unknown> | null | undefined
): boolean {
  if (!row) return false;
  if (row.isSupported === false) return false;
  if (row.communitySupported === false) return false;
  if (!deviceMatchesActiveBrand(row)) return false;
  return true;
}

function resolveCommunityCatalogKeyFromRow(
  row: Record<string, unknown>,
  category: "keyboard" | "mouse",
  allRows: Record<string, unknown>[]
): string {
  const lineId = catalogProductLineIdentity(row, category);
  const siblings = allRows.filter(
    (candidate) => catalogProductLineIdentity(candidate, category) === lineId
  );
  const canonical = pickCanonicalCatalogRow(siblings) ?? row;
  const snapshot = buildDeviceCatalogSnapshot(canonical);
  if (!snapshot) return "";
  return buildDeviceCatalogKey(snapshot);
}

/** 实机 → 配置广场型号键（复用配置表行定位，不区分有线/无线） */
export function resolveDeviceCatalogKeyFromEquipment(
  equipment: Record<string, unknown> | null | undefined,
  category: "keyboard" | "mouse" | "earphone"
): string | null {
  if (!equipment || typeof equipment !== "object") return null;

  const allRows = listCommunityCatalogRows(category);
  if (category === "earphone") {
    const deviceId = String(equipment.deviceId ?? "").trim();
    const vid = Number(equipment.vendorId);
    const pid = Number(equipment.productId);
    const row =
      allRows.find((r) => String(r.deviceId ?? "") === deviceId) ??
      allRows.find(
        (r) => Number(r.vendorId) === vid && Number(r.productId) === pid
      );
    if (!row || !isCommunitySupportedCatalogRow(row)) return null;
    const snapshot = buildDeviceCatalogSnapshot(row);
    return snapshot ? buildDeviceCatalogKey(snapshot) : null;
  }

  const row =
    category === "mouse"
      ? resolveMouseCatalogRowForEquipment(equipment)
      : resolveKeyboardCatalogRowForEquipment(equipment);
  if (!row || !isCommunitySupportedCatalogRow(row)) return null;

  const key = resolveCommunityCatalogKeyFromRow(row, category, allRows);
  return key || null;
}

/**
 * 实机 → 配置表型号键（不校验 communitySupported）。
 * 仅用于展示/合并配置，不作为关联应用等设备能力的准入条件。
 */
export function resolveSupportedDeviceCatalogKeyFromEquipment(
  equipment: Record<string, unknown> | null | undefined,
  category: "keyboard" | "mouse" | "earphone"
): string | null {
  if (!equipment || typeof equipment !== "object") return null;

  const allRows = listSupportedDeviceCatalogRows(category);
  if (category === "earphone") {
    const deviceId = String(equipment.deviceId ?? "").trim();
    const vid = Number(equipment.vendorId);
    const pid = Number(equipment.productId);
    const row =
      allRows.find((r) => String(r.deviceId ?? "") === deviceId) ??
      allRows.find(
        (r) => Number(r.vendorId) === vid && Number(r.productId) === pid
      );
    if (!row) return null;
    const snapshot = buildDeviceCatalogSnapshot(row);
    return snapshot ? buildDeviceCatalogKey(snapshot) : null;
  }

  const row =
    category === "mouse"
      ? resolveMouseCatalogRowForEquipment(equipment)
      : resolveKeyboardCatalogRowForEquipment(equipment);
  if (!row) return null;

  const key = resolveCommunityCatalogKeyFromRow(row, category, allRows);
  return key || null;
}

/** 社区配置 → 配置广场型号键（优先 payload._deviceCatalog + 配置表行定位） */
export function resolveDeviceCatalogKeyFromConfig(
  deviceCategory: "keyboard" | "mouse",
  configPayload?: Record<string, unknown> | null,
  deviceModel?: string
): string | null {
  const allRows = listCommunityCatalogRows(deviceCategory);
  const snapshot = readDeviceCatalogSnapshotFromPayload(configPayload ?? null);

  if (snapshot) {
    const row = resolveCatalogRowFromSnapshot(snapshot, deviceCategory);
    if (row) {
      const key = resolveCommunityCatalogKeyFromRow(
        row,
        deviceCategory,
        allRows
      );
      if (key) return key;
    }
    return buildDeviceCatalogKey(snapshot);
  }

  const displayname = String(deviceModel ?? "").trim();
  if (!displayname) return null;

  const sameNameRows = allRows.filter(
    (row) => resolveRowDisplayname(row) === displayname
  );
  if (sameNameRows.length === 1) {
    return resolveCommunityCatalogKeyFromRow(
      sameNameRows[0]!,
      deviceCategory,
      allRows
    );
  }

  return buildCommunityModelCatalogKey(deviceCategory, displayname);
}

export function communityDeviceCatalogKeysMatch(
  configKey: string | null | undefined,
  filterKey: string | null | undefined
): boolean {
  if (!configKey || !filterKey) return false;
  return configKey === filterKey;
}

/** 列表项常无完整 payload，catalogKey 可能与实机键格式不同；按 catalogKey 或 displayname 匹配 */
export function communityConfigMatchesConnectedCatalogKeys(
  config: {
    deviceCategory: "keyboard" | "mouse";
    deviceModel?: string;
    configPayload?: Record<string, unknown> | null;
  },
  connectedCatalogKeys: string[]
): boolean {
  if (!connectedCatalogKeys.length) return false;

  const configKey = resolveDeviceCatalogKeyFromConfig(
    config.deviceCategory,
    config.configPayload,
    config.deviceModel
  );

  for (const connectedKey of connectedCatalogKeys) {
    if (communityDeviceCatalogKeysMatch(configKey, connectedKey)) {
      return true;
    }
  }

  const configDisplayname = resolveCommunityDisplaynameFromCatalog(
    config.deviceCategory,
    config.configPayload,
    config.deviceModel
  );
  if (!configDisplayname) return false;

  return connectedCatalogKeys.some((connectedKey) => {
    const connectedDisplayname = resolveDisplaynameFromCatalogKey(
      config.deviceCategory,
      connectedKey
    );
    return (
      Boolean(connectedDisplayname) &&
      connectedDisplayname === configDisplayname
    );
  });
}

/** 配置广场 / 我的作品：每 displayname 一条（同型号多连接/多方案合并） */
export function listCommunityDeviceCatalogOptions(
  category: "keyboard" | "mouse" | "earphone"
): CommunityDeviceCatalogOption[] {
  const rows = listCommunityCatalogRows(category);
  if (category === "earphone") {
    const options: CommunityDeviceCatalogOption[] = [];
    const seen = new Set<string>();
    for (const row of rows) {
      const deviceId = String(row.deviceId ?? "").trim();
      const displayname = resolveRowDisplayname(row);
      if (!displayname || !deviceId || seen.has(deviceId)) continue;
      seen.add(deviceId);
      const snapshot = buildDeviceCatalogSnapshot(row);
      if (!snapshot) continue;
      options.push({
        key: buildDeviceCatalogKey(snapshot),
        label: displayname,
        displayname,
        snapshot,
      });
    }
    return options.sort((a, b) =>
      a.displayname.localeCompare(b.displayname, "zh-CN", {
        sensitivity: "base",
      })
    );
  }

  const byDisplayname = new Map<string, Record<string, unknown>[]>();

  for (const row of rows) {
    const displayname = resolveRowDisplayname(row);
    if (!displayname) continue;
    const group = byDisplayname.get(displayname) ?? [];
    group.push(row);
    byDisplayname.set(displayname, group);
  }

  const options: CommunityDeviceCatalogOption[] = [];

  for (const [displayname, nameRows] of byDisplayname) {
    const canonical = pickCanonicalCatalogRow(nameRows);
    if (!canonical) continue;
    const snapshot = buildDeviceCatalogSnapshot(canonical);
    if (!snapshot) continue;

    options.push({
      key: buildDeviceCatalogKey(snapshot),
      label: formatDeviceDisplayLabel(displayname),
      displayname,
      snapshot,
    });
  }

  return options.sort((a, b) =>
    a.displayname.localeCompare(b.displayname, "zh-CN", { sensitivity: "base" })
  );
}

export function findCommunityDeviceCatalogOption(
  category: "keyboard" | "mouse" | "earphone",
  catalogKey: string
): CommunityDeviceCatalogOption | undefined {
  const options = listCommunityDeviceCatalogOptions(category);
  const direct = options.find((item) => item.key === catalogKey);
  if (direct) return direct;

  const prefix = `${MODEL_KEY_PREFIX}${category}:`;
  const legacyName = catalogKey.startsWith(prefix)
    ? catalogKey.slice(prefix.length).trim()
    : "";
  if (legacyName) {
    return options.find((item) => item.displayname === legacyName);
  }

  if (category === "earphone") {
    return options.find(
      (item) =>
        String(item.snapshot.deviceId ?? "") === catalogKey ||
        item.displayname === catalogKey
    );
  }

  const rows = listCommunityCatalogRows(category);
  for (const row of rows) {
    const key = resolveCommunityCatalogKeyFromRow(row, category, rows);
    if (key !== catalogKey) continue;
    const displayname = resolveRowDisplayname(row);
    const byName = options.find((item) => item.displayname === displayname);
    if (byName) return byName;
  }

  return undefined;
}

/** 向后端 query 仍传 displayname 时使用（筛选用 catalogKey） */
export function resolveDisplaynameFromCatalogKey(
  category: "keyboard" | "mouse" | "earphone",
  catalogKey: string
): string | null {
  const fromOption = findCommunityDeviceCatalogOption(category, catalogKey);
  if (fromOption) return fromOption.displayname;

  const prefix = `${MODEL_KEY_PREFIX}${category}:`;
  if (catalogKey.startsWith(prefix)) {
    const displayname = catalogKey.slice(prefix.length).trim();
    return displayname || null;
  }

  return null;
}

/** 配色库 / 预览图：由配置表快照取 displayname，不做模糊匹配 */
export function resolveCommunityDisplaynameFromCatalog(
  deviceCategory: "keyboard" | "mouse",
  configPayload?: Record<string, unknown> | null,
  deviceModel?: string
): string {
  const snapshot = readDeviceCatalogSnapshotFromPayload(configPayload ?? null);
  if (snapshot) {
    const row = resolveCatalogRowFromSnapshot(snapshot, deviceCategory);
    if (row?.displayname) return String(row.displayname);
    if (snapshot.displayname) return String(snapshot.displayname);
  }
  return String(deviceModel ?? "").trim();
}
